import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    Card,
    Table,
    notification,
    Tooltip,
    Pagination,
    Input,
    Image,
    Select,
    message,
    Skeleton
} from "antd";
import apiClient from "../../services/api";
import { baseUrl } from "../../config";
import { skeletonData } from '../../components/skeletons/Contact';
import moment from "moment";
import { useSidebar } from '../../SidebarContext';
import { handleApiError, handleApiInfo } from "../../utils/errorHandler";
import logger from "../../utils/logger";

const { Search } = Input;

function ContactUsList() {
    const [userList, setUserList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalUsers, setTotalUsers] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const { setContactCount } = useSidebar();

    const columns = [
        {
            title: "#",
            dataIndex: "index",
            key: "index",
        },
        {
            title: "First Name",
            dataIndex: "firstName",
            key: "firstName",
        },
        {
            title: "Last Name",
            dataIndex: "lastName",
            key: "lastName",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Browse Image",
            dataIndex: "browseImage",
            key: "browseImage",
        },
        {
            title: "Message",
            dataIndex: "message",
            key: "message"
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
        },
        {
            title: 'Status',
            dataIndex: 'isSolve',
            fixed: 'right',
            key: 'isSolve',
            render: (value, record) => {
                if (record.isSkeleton) {
                    return <Skeleton active title={false} paragraph={{ rows: 1, width: "100px" }} />;
                }
                const isActive = record.isSolve === true;

                return (
                    <Select
                        value={isActive ? "true" : "false"}
                        className={isActive ? "status-color-active" : "status-color-inactive"}
                        style={{
                            width: 120
                        }}
                        onChange={(newStatus) => handleStatusChange(record._id, newStatus)}
                    >
                        <Select.Option value="true">Solve</Select.Option>
                        <Select.Option value="false">Pending</Select.Option>
                    </Select>
                );
            }
        }
    ];

    useEffect(() => {
        readContactUs();
    }, [setContactCount]) // eslint-disable-line react-hooks/exhaustive-deps

    const readContactUs = async () => {
        try {
            const response = await apiClient.patch('/api/contact-us', {});
            if (response.status === 200) {
                setContactCount(0);
            } else {
                logger.error('Failed to read contact us:', response?.data?.message);
            }
        } catch (error) {
            logger.error('Error reading contact us:', error);
        }
    };

    useEffect(() => {
        getContactList();
    }, [searchTerm, currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

    async function getContactList() {
        try {
            setLoading(true);

            const params = {
                page: currentPage,
                items: pageSize,
            };

            // Check if search is not null or empty, then add it to params
            if (searchTerm && searchTerm.trim() !== '') {
                params.q = searchTerm;
                params.fields = 'firstName,lastName,email';
            }

            const response = await apiClient.get('/api/contact-us', { params });
            if (response.data.success) {
                setUserList(response.data.result);
                setTotalUsers(response.data.pagination.count);
            } else {
                setUserList([]);
            }
        } catch (error) {
            handleApiError(error);
            logger.error("Error fetching contact list:", error);
            setUserList([]);
        } finally {
            setLoading(false);
        }
    }

    const handlePageChange = (page, pageSize) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    const handleSearch = (value) => {
        if (value.length >= 3 || value.length === 0) {
            setSearchTerm(value);
            setCurrentPage(1);
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            const statusLatest = newStatus === "true" ? true : false;
            const response = await apiClient.patch(
                `/api/contact-us/status/${id}`,
                { status: statusLatest }
            );

            if (response.status === 200) {
                message.success('Status updated successfully');
                setUserList(prev =>
                    prev.map(user =>
                        user._id === id ? { ...user, isSolve: statusLatest } : user
                    )
                );
            }
        } catch (error) {
            logger.error("Status update error:", error);
            setUserList(prev =>
                prev.map(user =>
                    user._id === id ? { ...user, isSolve: user.isSolve } : user
                )
            );
            message.error('Failed to update status');
        }
    };

    return (
        <>
            <div className="tabled">
                <Row gutter={[24, 0]}>
                    <Col xs="24" xl={24}>
                        <Card
                            bordered={false}
                            className="criclebox tablespace mb-24"
                            title="Contact Us"
                            extra={<Search placeholder="Search by name or email" onSearch={handleSearch} allowClear />}
                        >
                            <div className="table-responsive">
                                <Table
                                    columns={columns}
                                    dataSource={loading ? skeletonData : userList.map((user, index) => ({
                                        index: (currentPage - 1) * pageSize + index + 1,
                                        key: user._id,
                                        _id: user._id,
                                        firstName: (
                                            <p>{user.firstName}</p>
                                        ),
                                        lastName: (
                                            <p>{user.lastName}</p>
                                        ),
                                        email: (
                                            <p>{user.email}</p>
                                        ),
                                        browseImage: user.browseImage ? <Image width="60px" src={baseUrl+'/'+user.browseImage} /> : '-',
                                        message: (
                                            <Tooltip title={user.message}> {/* Full message shown on hover */}
                                                <p style={{
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    maxWidth: "200px" // Adjust width as needed
                                                }}>
                                                    {user.message ? user.message : '-'}
                                                </p>
                                            </Tooltip>
                                        ),
                                        createdAt: (
                                            <p>{moment(user.createdAt).format("MMMM DD, YYYY hh:mm A")}</p>
                                        ),
                                        isSolve: user.isSolve
                                    }))}
                                    pagination={false} // Disable default pagination inside the table
                                    scroll={{ x: 'max-content' }}
                                />
                            </div>
                            {/* Keep pagination outside the scrollable div */}
                            <div className="pagination-container">
                                <Pagination
                                    current={currentPage}
                                    pageSize={pageSize}
                                    total={totalUsers}
                                    onChange={handlePageChange}
                                />
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
}

export default ContactUsList;
